// src/Pages/WritePost/index.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './index.css';

const WritePost = () => {
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [category, setCategory] = useState('');
  const [excerpt, setExcerpt] = useState('');
  
  // 可选的分类选项
  const categories = [
    '技术分享',
    '生活感悟',
    '读书笔记',
    '旅行游记',
    '其他'
  ];

  // 添加标签
  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  // 移除标签
  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // 发布文章
  const publishPost = async () => {
    if (!title.trim()) {
      alert('请输入文章标题');
      return;
    }
    
    if (!content.trim()) {
      alert('请输入文章内容');
      return;
    }

    setIsPublishing(true);

    try {
      // 创建文章对象
      const postData = {
        id: Date.now().toString(), // 简单的ID生成
        title: title.trim(),
        content: content.trim(),
        tags,
        category,
        excerpt: excerpt.trim(),
        createdAt: new Date().toISOString(),
        author: '当前用户', // 这里应该是从用户状态获取
        status: 'published',
        viewCount: 0,
        likeCount: 0
      };

      // 从本地存储获取现有文章
      const storedPosts = localStorage.getItem('blogPosts');
      let posts = storedPosts ? JSON.parse(storedPosts) : [];
      
      // 添加新文章
      posts.push(postData);
      
      // 保存回本地存储
      localStorage.setItem('blogPosts', JSON.stringify(posts));
      
      alert('文章发布成功！');
      
      // 重置表单
      setTitle('');
      setContent('');
      setTags([]);
      setCategory('');
      setExcerpt('');
      
      // 导航到文章列表页面
      navigate('/posts');
    } catch (error) {
      console.error('发布失败:', error);
      alert('发布失败，请重试');
    } finally {
      setIsPublishing(false);
    }
  };

  // 保存草稿
  const saveDraft = async () => {
    if (!title.trim() && !content.trim()) {
      alert('请至少输入标题或内容');
      return;
    }

    try {
      const draftData = {
        id: Date.now().toString(), // 简单的ID生成
        title: title.trim(),
        content: content.trim(),
        tags,
        category,
        excerpt: excerpt.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: '当前用户',
        status: 'draft',
        viewCount: 0,
        likeCount: 0
      };

      // 从本地存储获取现有文章
      const storedPosts = localStorage.getItem('blogPosts');
      let posts = storedPosts ? JSON.parse(storedPosts) : [];
      
      // 添加草稿
      posts.push(draftData);
      
      // 保存回本地存储
      localStorage.setItem('blogPosts', JSON.stringify(posts));
      
      alert('草稿已保存！');
    } catch (error) {
      console.error('保存草稿失败:', error);
      alert('保存草稿失败，请重试');
    }
  };

  return (
    <div className="write-post-container">
      <div className="write-post-header">
        <h1>写文章</h1>
        <div className="post-actions">
          <button 
            className="save-draft-btn" 
            onClick={saveDraft}
            disabled={isPublishing}
          >
            保存草稿
          </button>
          <button 
            className="publish-btn" 
            onClick={publishPost}
            disabled={isPublishing}
          >
            {isPublishing ? '发布中...' : '发布'}
          </button>
        </div>
      </div>

      <div className="write-post-form">
        {/* 文章标题 */}
        <div className="form-group">
          <label htmlFor="title">标题 *</label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="请输入文章标题"
            className="title-input"
          />
        </div>

        {/* 文章分类 */}
        <div className="form-group">
          <label htmlFor="category">分类</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="category-select"
          >
            <option value="">选择分类</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* 文章摘要 */}
        <div className="form-group">
          <label htmlFor="excerpt">摘要</label>
          <textarea
            id="excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="文章摘要（可选）"
            rows="3"
            className="excerpt-input"
          />
        </div>

        {/* 文章标签 */}
        <div className="form-group">
          <label>标签</label>
          <div className="tags-input-container">
            <div className="tags-list">
              {tags.map((tag, index) => (
                <span key={index} className="tag-item">
                  {tag}
                  <button 
                    type="button" 
                    className="remove-tag-btn"
                    onClick={() => removeTag(tag)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="add-tag-input">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                placeholder="添加标签（按回车确认）"
                className="tag-input"
              />
              <button type="button" className="add-tag-btn" onClick={addTag}>
                添加
              </button>
            </div>
          </div>
        </div>

        {/* 文章内容 */}
        <div className="form-group">
          <label htmlFor="content">内容 *</label>
          <textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="开始写作..."
            rows="20"
            className="content-input"
          />
        </div>
      </div>
    </div>
  );
};

export default WritePost;